/**
 * Fills the header bell and decides when the alarm may arm — app-wide.
 *
 * This is the hook that makes the bell work off `/map`. It replaces the map's
 * `useAlertNotifyBridge`, and with it the SOURCE changes: the bell used to be fed
 * from History (`GET /api/alerts/`), which is bounded by the global date picker,
 * so the operator's choice of review window silently decided which alerts could
 * ever notify them. The Queue is the right source — "what requires a decision
 * now?" is exactly the bell's question, and it is unbounded by date.
 *
 * Two decisions live here, and they are deliberately separate:
 *
 *   DISPLAY  — every queue read is merged into the notify store. Cheap, always
 *              safe, never makes noise.
 *   SOUND    — governed by MIN-223's six conditions, evaluated by
 *              `utils/sound-eligibility.ts` against the SAME rows that were just
 *              merged, then armed explicitly. Never inferred from the rows alone.
 *
 * Ordering matters and is why both live in one effect: `notify()` must have put
 * an alert into `items`/`newIds` before `armChime` can point at it, or the badge
 * and the sound would disagree for a tick.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  PROBE_QUANTUM_MS,
  useAlertQueue,
  useRecentAlertProbe,
} from "@/alerts/api/queries/alerts-workspace.queries.ts";
import { useActiveAlertScope } from "@/alerts/hooks/use-active-alert-scope.ts";
import { useAlertLiveStore, type AlertLiveHint } from "@/alerts/stores/use-alert-live-store.ts";
import { useAlertNotifyStore } from "@/alerts/stores/use-alert-notify-store.ts";
import { eligibleAlertIds } from "@/alerts/utils/sound-eligibility.ts";
import type {
  AlertQueueFilterState,
  AlertQueueRow,
  AlertRow,
} from "@/alerts/types/alerts-workspace.ts";

/**
 * Rows the bell asks the queue for.
 *
 * It was 200 — the API's maximum — on the reasoning that a hinted alert absent
 * from the loaded page cannot sound, so the widest legal page produces the most
 * sound. Measured, that page is ~180 KB and it was fetched every 20 seconds:
 * 660 KB/min, 78% of ALL traffic on /map, several times what the video itself
 * uses. It was starving the thing an operator actually watches.
 *
 * And it bought nothing. The queue is ranked oldest-first over a backlog in the
 * thousands, so a new alert is not on page one at 200 rows any more than at 50 —
 * arrivals are detected by the rolling probe, not here. The bell reads a count
 * and a short list off this; 50 rows serve both.
 */
export const NOTIFY_QUEUE_LIMIT = 50;

/**
 * How often the bell re-reads the queue. Slower than the workspace's own 20s:
 * this feeds a badge and a backlog list, neither of which changes meaningfully
 * within a minute, and a live arrival reaches the bell through the Firestore
 * hint and the arrival probe rather than by waiting for this.
 */
export const NOTIFY_QUEUE_POLL_MS = 60_000;

const toNotification = (row: AlertQueueRow | AlertRow) => ({
  id: row.id,
  type: row.type,
  status: row.status,
  machineName: row.machine_name || row.device_mxid,
  deviceMxid: row.device_mxid,
  timestamp: row.timestamp,
});

export const useAlertNotifyFeed = (): void => {
  const { locationId, scopeKey } = useActiveAlertScope();

  // No machine/type narrowing: the bell speaks for the whole location. The
  // workspace's own filters belong to the table the operator is reading, not to
  // an alarm that has to reach them in another room.
  const filterState = useMemo<AlertQueueFilterState | null>(
    () =>
      locationId == null
        ? null
        : { scope: { location_id: locationId }, limit: NOTIFY_QUEUE_LIMIT },
    [locationId],
  );

  const { data } = useAlertQueue(filterState, NOTIFY_QUEUE_POLL_MS);

  // Rolling clock for the arrival probe. Ticks once a quantum rather than on
  // every render, because the window bounds are part of the probe's query key.
  const [nowBucket, setNowBucket] = useState(
    () => Math.floor(Date.now() / PROBE_QUANTUM_MS) * PROBE_QUANTUM_MS,
  );
  useEffect(() => {
    const id = setInterval(
      () => setNowBucket(Math.floor(Date.now() / PROBE_QUANTUM_MS) * PROBE_QUANTUM_MS),
      PROBE_QUANTUM_MS,
    );
    return () => clearInterval(id);
  }, []);

  const { data: probe } = useRecentAlertProbe(locationId, nowBucket);

  const notify = useAlertNotifyStore((s) => s.notify);
  const armChime = useAlertNotifyStore((s) => s.armChime);

  // Read the hint map imperatively. Subscribing would re-run this effect when a
  // hint lands but BEFORE the queue re-read it triggers has resolved, and judging
  // eligibility against the pre-hint page is exactly the stale comparison the
  // ticket's condition 3 exists to prevent.
  const liveStore = useAlertLiveStore;

  // Hints already put through the gate. Bounded by the store's own TTL pruning:
  // an id can only re-enter after it has aged out of `recentHints`, and a hint
  // that is refused (wrong cycle, filtered, closed) must not be reconsidered on
  // the next poll — that would turn one refused hint into a poll-rate retry loop.
  const judgedRef = useRef<Set<string>>(new Set());
  // Whether this scope has been seeded. Until it has, everything on screen is
  // backlog: "initial Queue hydration" is the first entry in MIN-223's
  // do-not-play list.
  const seededScopeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!scopeKey) return;

    // ARRIVALS come from the rolling History probe (and, when the channel is up,
    // from Firestore hints). NOT from the queue: it is ranked oldest-first with
    // a page cap, so on a backlog in the thousands a new alert is on no page the
    // bell will ever hold. The queue contributes the standing backlog for
    // context and, above all, the authoritative `count`.
    const recent = probe?.items ?? [];
    const queueRows = data?.items ?? [];
    const rows = [...queueRows, ...recent];
    // Nothing loaded yet from EITHER source. Deliberately not gated on the queue
    // alone: doing that meant an empty or slow queue swallowed every arrival the
    // probe had already found, which is the one thing this hook exists to
    // deliver.
    if (rows.length === 0) return;

    const isFirstBatchOfScope = seededScopeRef.current !== scopeKey;
    if (isFirstBatchOfScope) {
      seededScopeRef.current = scopeKey;
      judgedRef.current = new Set();
    }

    // DISPLAY. On a new scope this seeds silently by the store's own contract.
    const freshPending = notify(rows.map(toNotification), scopeKey);

    // Publish the canonical workload figure + reconciliation time for the bell.
    // Queue-only — the probe reads a 30-minute window and knows nothing about
    // the size of the backlog.
    const liveScope = liveStore.getState().activeScopeKey;
    if (liveScope && data) liveStore.getState().acceptQueue(liveScope, data.count, data.asOf);

    // SOUND. Never on the batch that seeded the scope.
    if (isFirstBatchOfScope) {
      // Everything visible now is pre-existing work; it must not be reconsidered
      // when the next poll arrives either.
      judgedRef.current = new Set(Object.keys(liveStore.getState().recentHints));
      return;
    }

    const { recentHints } = liveStore.getState();
    const pendingHints = Object.values(recentHints).filter(
      (hint: AlertLiveHint) => !judgedRef.current.has(hint.alertId),
    );
    pendingHints.forEach((hint) => judgedRef.current.add(hint.alertId));

    // Path 1 — the ticket's rule, confirmed against everything REST just told us
    // (queue page ∪ probe window), not the queue alone.
    //
    // MIN-223 words condition 3 as "a fresh Queue response still contains the
    // Alert". Taken literally that condition can never be met: the queue is
    // oldest-first with a page cap, so the alert a hint names is precisely the
    // one not on it. The condition's PURPOSE is that canonical REST state
    // confirms the alert exists, is still Pending and is on the right review
    // cycle — and the probe is canonical REST state. Confirming against both is
    // what makes the Firestore path work at all rather than being dead by
    // construction.
    const hintArmable = eligibleAlertIds(pendingHints, rows);

    // Path 2 — REST-confirmed arrival. A previously-unknown alert that canonical
    // state reports as Pending and that is younger than the store's recency
    // window satisfies conditions 3, 4 and 6 on its own; only the hint-derived 1
    // and 5 are unavailable. That is an identified alert, not the "Queue count
    // difference" the ticket rules out.
    //
    // NOT gated on the channel being down. It is tempting to treat this as a
    // fallback, but the queue's oldest-first ranking defeats condition 3 for any
    // location with a backlog deeper than one page — so a HEALTHY channel is
    // equally unable to ring for the 470th pending alert. Gating this on a dead
    // channel would silence the alarm again the day the Firestore rules are
    // fixed. Double-arming is harmless: `armChime` unions ids, and the alarm is
    // one repeating pattern however many alerts qualify.
    //
    // `notify` already narrowed its return to fresh, still-Pending, recent ones.
    const restArmable = freshPending.map((a) => a.id);

    armChime([...new Set([...hintArmable, ...restArmable])]);
  }, [data, probe, scopeKey, notify, armChime, liveStore]);
};
